// src/services/authService.ts
// Centralized authentication helpers (Google & Apple) using Firebase Auth.
// Keeps UI components lean and provides a single place for auth-related logic.

// Use the web-extension entry point: it omits signInWithPopup/Redirect and
// their remotely-hosted gapi/reCAPTCHA loaders, which Manifest V3 (Chrome Web
// Store) forbids. The extension signs in via launchWebAuthFlow / relay /
// custom-token instead — none of which need popup or redirect.
import {
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth/web-extension';
import type { User } from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import { saveToStorageAsync, removeFromStorageAsync } from './storage';
import { error as logError } from '../utils/logger';

export interface AuthUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  providerId: string;
}

const STORAGE_KEY = 'authUser';

// Utility to map Firebase User to lightweight serializable object
const mapUser = (user: User): AuthUser => ({
  uid: user.uid,
  displayName: user.displayName,
  email: user.email,
  photoURL: user.photoURL,
  providerId: user.providerData[0]?.providerId || 'unknown',
});

export const getStoredUser = (): AuthUser | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch (e) {
    logError('Failed to read stored user synchronously:', e);
    return null;
  }
};

const persistUser = async (user: User): Promise<void> => {
  // prefer async storage for durability
  try {
    await saveToStorageAsync<AuthUser>(STORAGE_KEY, mapUser(user));
  } catch (e) {
    // fallback to synchronous write to localStorage if async fails
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mapUser(user)));
    } catch (err) {
      logError('Failed to persist user to storage:', err);
    }
  }
};

const clearUser = async (): Promise<void> => {
  try {
    await removeFromStorageAsync(STORAGE_KEY);
  } catch (e) {
    try {
      // fallback: remove from localStorage directly
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      logError('Failed to clear stored user:', err);
    }
  }
};

export interface AuthResult {
  user: AuthUser | null;
  error?: string;
}

// Popup/redirect OAuth is intentionally NOT available in production builds (the
// web-extension Firebase entry omits it — MV3 forbids the remote gapi/reCAPTCHA
// scripts those flows load). Production never used them: the extension uses
// launchWebAuthFlow (Chrome) / the hosted relay (Safari), and the account web
// app uses custom-token handoff.
//
// Localhost dev (`npm start` in a plain browser tab) has no identity API and no
// relay, so it gets a real popup sign-in via the full `firebase/auth` entry,
// loaded dynamically. The import sits inside a statically-false branch in
// production (webpack's parser constant-folds the inline NODE_ENV check before
// building the chunk graph), so no popup/gapi code ever reaches the shipped
// bundle — the CWS/MV3 constraint that motivated the web-extension entry holds.
const NO_POPUP_MSG =
  'Direct popup sign-in is unavailable in this build. Load the extension, or set REACT_APP_PREVIEW_WITHOUT_AUTH=true for local development.';

const devPopupSignIn = async (providerId: 'google.com' | 'apple.com'): Promise<AuthResult> => {
  if (process.env.NODE_ENV === 'development') {
    try {
      const {
        signInWithPopup,
        browserPopupRedirectResolver,
        GoogleAuthProvider: DevGoogleProvider,
        OAuthProvider: DevOAuthProvider,
      } = await import('firebase/auth');

      // Providers must come from the dynamically imported module — the popup
      // flow instanceof-checks them against its own class identities.
      let provider;
      if (providerId === 'google.com') {
        const p = new DevGoogleProvider();
        p.setCustomParameters({ prompt: 'select_account' });
        provider = p;
      } else {
        const p = new DevOAuthProvider('apple.com');
        p.addScope('email');
        p.addScope('name');
        provider = p;
      }

      // The shared auth instance comes from the web-extension entry (no default
      // popup resolver), so pass the resolver explicitly.
      const result = await signInWithPopup(
        auth as unknown as Parameters<typeof signInWithPopup>[0],
        provider,
        browserPopupRedirectResolver,
      );
      const user = result.user as unknown as User;
      await persistUser(user);
      return { user: mapUser(user) };
    } catch (e) {
      logError('Dev popup sign-in failed:', e);
      return { user: null, error: e instanceof Error ? e.message : String(e) };
    }
  }
  return { user: null, error: NO_POPUP_MSG };
};

export const signInWithGoogle = async (): Promise<AuthResult> => {
  if (process.env.NODE_ENV === 'development') {
    return devPopupSignIn('google.com');
  }
  logError('signInWithGoogle: popup sign-in is unavailable in the web-extension build');
  return { user: null, error: NO_POPUP_MSG };
};

export const signInWithApple = async (): Promise<AuthResult> => {
  if (process.env.NODE_ENV === 'development') {
    return devPopupSignIn('apple.com');
  }
  logError('signInWithApple: popup sign-in is unavailable in the web-extension build');
  return { user: null, error: NO_POPUP_MSG };
};

export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } finally {
    await clearUser();
  }
};

// Subscribe to live auth state changes (e.g., app initialization)
export const authStateListener = (callback: (user: AuthUser | null) => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const mapped = mapUser(firebaseUser);
      await persistUser(firebaseUser);
      callback(mapped);
    } else {
      await clearUser();
      callback(null);
    }
  });
};

export const getCurrentUser = (): Promise<AuthUser | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      unsubscribe();
      resolve(firebaseUser ? mapUser(firebaseUser) : null);
    });
  });
};

// Simple feature detection / readiness check
export const isAuthConfigured = (): boolean => !!auth?.app;

// Optionally expose providers for advanced flows
export const providers = {
  google: () => new GoogleAuthProvider(),
  apple: () => {
    const p = new OAuthProvider('apple.com');
    p.addScope('email');
    p.addScope('name');
    return p;
  },
};

const authService = {
  signInWithGoogle,
  signInWithApple,
  signOutUser,
  authStateListener,
  getCurrentUser,
  getStoredUser,
  isAuthConfigured,
  providers,
};

export default authService;
